if (!customElements.get('bm-product')) {
  customElements.define('bm-product', class BMProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      const config = this.querySelector('[data-bm-config]');
      if (!config) return;
      try { this.config = JSON.parse(config.textContent); } catch { return; }
      if (!this.config.products?.length || !this.querySelector('form')) return;
      this.initialized = true;
      this.abort = new AbortController();
      this.form = this.querySelector('form');
      this.products = this.config.products;
      this.product = this.products.find(product => product.id === this.config.productId);
      this.mode = 'single';
      this.busy = false;
      this.switching = false;
      this.form.noValidate = true;
      this.addEventListener('change', this.onChange.bind(this), { signal: this.abort.signal });
      this.addEventListener('click', this.onClick.bind(this), { signal: this.abort.signal });
      this.form.addEventListener('submit', this.onSubmit.bind(this), { signal: this.abort.signal });
      this.galleryIndex = 0;
      const gallery = this.querySelector('[data-bm-gallery]');
      gallery?.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault(); this.stepPhoto(event.key === 'ArrowLeft' ? -1 : 1);
        }
      }, { signal: this.abort.signal });
      this.setupGalleryGestures();
      this.rows = [...this.querySelectorAll('[data-bm-row]')];
      const bundleNames = { '100-bill': 'Money', 'belt-1': 'Money', 'pink-sprinkle-drip': 'Pink drip', 'pink-drip': 'Pink drip', 'blue-sprinkle-drip': 'Blue drip', 'belt-2': 'Blue drip', bubblegum: 'Bubblegum', 'bubble-gum': 'Bubblegum', 'red-white-blue-drip': 'RWB' };
      this.rows.forEach((row, index) => {
        const select = row.querySelector('[data-bm-row-product]');
        select.replaceChildren();
        this.products.filter(product => !product.requires_selling_plan).forEach(product => {
          const option = new Option(bundleNames[product.handle] || product.title, String(product.id));
          option.title = product.title;
          option.disabled = !product.available;
          select.add(option);
        });
        const choices = this.products.filter(product => product.available && !product.requires_selling_plan);
        if (choices.length) select.value = String(choices[index % choices.length].id);
        this.updateRow(row);
      });
      const modes = this.querySelector('[data-bm-modes]');
      if (modes && this.rows.every(row => row.querySelector('[data-bm-row-product]').options.length)) {
        modes.hidden = false;
        if (this.config.bundleEnabled && new URLSearchParams(location.search).get('bundle') === '1') {
          modes.querySelector('[value="bundle"]').checked = true;
          this.setMode('bundle');
        }
      }
      this.classList.add('is-ready');
      if (this.pendingSelection) this.restoreSelection(this.pendingSelection);
      this.setupDesignPicker();
      this.update();
      this.updateShipping(this.config.cartTotal);
      if (this.config.shippingEnabled) this.refreshCart();
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
          if (event.source !== 'bm-product') this.refreshCart();
        });
      }
      if ('IntersectionObserver' in window) {
        this.stickyObserver = new IntersectionObserver(([entry]) => {
          this.querySelector('[data-bm-sticky]').hidden = entry.intersectionRatio >= 0.5;
        }, { threshold: [0, 0.5] });
        this.stickyObserver.observe(this.querySelector('[data-bm-submit]'));
      } else this.querySelector('[data-bm-sticky]').hidden = false;
    }

    setupGalleryGestures() {
      const gallery = this.querySelector('[data-bm-gallery]');
      if (!gallery) return;
      let gesture = null;
      const controls = 'button,a,input,select,textarea,summary,[contenteditable="true"]';
      const options = { passive: true, signal: this.abort.signal };
      gallery.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'touch') return;
        if (gesture || event.isPrimary === false) { gesture = null; return; }
        if (event.target.closest(controls)) return;
        gesture = { id: event.pointerId, x: event.clientX, y: event.clientY };
      }, options);
      gallery.addEventListener('pointermove', event => {
        if (!gesture || event.pointerId !== gesture.id) return;
        const dx = Math.abs(event.clientX - gesture.x), dy = Math.abs(event.clientY - gesture.y);
        if (dy >= 12 && dy > dx * 1.2) gesture = null;
      }, options);
      gallery.addEventListener('pointerup', event => {
        if (!gesture || event.pointerId !== gesture.id) return;
        const start = gesture; gesture = null;
        const dx = event.clientX - start.x, dy = event.clientY - start.y;
        if (Math.abs(dx) >= 45 && Math.abs(dx) > Math.abs(dy) * 1.5) this.stepPhoto(dx < 0 ? 1 : -1);
      }, options);
      gallery.addEventListener('pointercancel', event => {
        if (event.pointerId === gesture?.id) gesture = null;
      }, options);
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.designRequest?.abort();
      this.designSequence = (this.designSequence || 0) + 1;
      if (this.designScrollFrame) cancelAnimationFrame(this.designScrollFrame);
      this.unsubscribe?.();
      this.stickyObserver?.disconnect();
      this.initialized = false;
    }

    setupDesignPicker() {
      const picker = this.querySelector('[data-bm-design-picker]');
      const mobile = this.querySelector('[data-bm-mobile-designs]');
      if (!picker || !mobile) return;
      this.currentURL = new URL(location.href);
      this.designHome = picker.parentNode;
      this.designNext = picker.nextSibling;
      this.designMedia = matchMedia('(max-width: 749px)');
      const shortNames = { '100-bill': 'Money', 'pink-sprinkle-drip': 'Pink drip', 'blue-sprinkle-drip': 'Blue drip', bubblegum: 'Bubblegum', 'red-white-blue-drip': 'RWB' };
      this.querySelectorAll('[data-bm-design-link]').forEach(link => {
        const label = link.querySelector(':scope > span');
        if (label) { link.dataset.fullLabel = label.textContent; link.dataset.shortLabel = shortNames[link.dataset.designKey] || label.textContent; }
      });
      const move = () => {
        if (this.designMedia.matches) mobile.append(picker);
        else this.designHome.insertBefore(picker, this.designNext);
        this.querySelectorAll('[data-bm-design-link]').forEach(link => {
          const label = link.querySelector(':scope > span');
          if (label) label.textContent = this.designMedia.matches ? link.dataset.shortLabel : link.dataset.fullLabel;
        });
      };
      move();
      this.designMedia.addEventListener('change', move, { signal: this.abort.signal });
      window.addEventListener('popstate', this.onHistoryChange.bind(this), { signal: this.abort.signal });
      this.rememberSelection();
    }

    captureSelection() {
      const variant = this.singleVariant();
      return {
        productId: this.product.id, mode: this.mode,
        single: variant ? { id: variant.id, options: variant.options } : null,
        rows: this.rows.map(row => ({ productId: this.rowProduct(row)?.id, variantId: this.rowVariant(row)?.id, options: this.rowVariant(row)?.options }))
      };
    }

    restoreSelection(selection) {
      if (!selection) return;
      const previous = selection.single;
      const selected = previous && this.product.variants.find(variant => variant.available && (
        selection.productId === this.product.id ? variant.id === previous.id :
          variant.options.length === previous.options.length && variant.options.every((value, i) => value === previous.options[i])
      ));
      this.form.querySelectorAll('[name="id"]').forEach(input => { input.checked = Boolean(selected && String(selected.id) === input.value); });
      selection.rows?.forEach((saved, index) => {
        const row = this.rows[index];
        const changedPrimary = index === 0 && saved.productId === selection.productId && this.product.id !== selection.productId;
        const product = this.products.find(product => product.id === (changedPrimary ? this.product.id : saved.productId) && !product.requires_selling_plan);
        if (!row || !product) return;
        row.querySelector('[data-bm-row-product]').value = String(product.id);
        this.updateRow(row);
        const variant = product.variants.find(variant => variant.available && (changedPrimary
          ? saved.options && variant.options.length === saved.options.length && variant.options.every((value, i) => value === saved.options[i])
          : variant.id === saved.variantId));
        if (variant) row.querySelector('[data-bm-row-variant]').value = String(variant.id);
      });
      const bundle = selection.mode === 'bundle' && this.config.bundleEnabled && !this.config.requiresSellingPlan;
      const mode = this.querySelector('[name="bm-mode"][value="' + (bundle ? 'bundle' : 'single') + '"]');
      if (mode) mode.checked = true;
      this.setMode(bundle ? 'bundle' : 'single');
    }

    rememberSelection() {
      if (!this.currentURL) return;
      try {
        history.replaceState({ ...history.state, bmProduct: { url: this.currentURL.href, selection: this.captureSelection() } }, '', location.href);
      } catch { /* Navigation still works if browser history is unavailable. */ }
    }

    onHistoryChange(event) {
      const url = new URL(location.href);
      this.switchDesign(url, { historyMode: 'none', selection: event.state?.bmProduct?.selection });
    }

    designFeedback(message, failed = false) {
      const feedback = this.querySelector('[data-bm-design-feedback]');
      if (!feedback) return;
      feedback.textContent = message;
      feedback.classList.toggle('is-error', failed);
      if (!failed) { const fallback = this.querySelector('[data-bm-design-fallback]'); if (fallback) fallback.hidden = true; }
    }

    async switchDesign(destination, { historyMode = 'push', selection = null } = {}) {
      if (this.busy) {
        this.designFeedback('Adding to your bag. Change designs when it finishes.');
        if (historyMode === 'none' && this.currentURL) { history.replaceState(history.state, '', this.currentURL); this.rememberSelection(); }
        return false;
      }
      const url = new URL(destination, location.href);
      if (url.origin !== location.origin) return false;
      this.designRequest?.abort();
      const sequence = this.designSequence = (this.designSequence || 0) + 1;
      this.switching = false;
      if (url.pathname === this.currentURL?.pathname) {
        if (selection) this.restoreSelection(selection);
        if (historyMode === 'none') this.currentURL = url;
        this.removeAttribute('aria-busy');
        this.designFeedback('Selected: ' + this.product.title); this.update(); return true;
      }
      this.designRequest = new AbortController();
      this.switching = true;
      this.setAttribute('aria-busy', 'true');
      this.designFeedback('Loading your design…');
      this.update();
      const requestURL = new URL(url);
      requestURL.searchParams.set('section_id', this.dataset.sectionId);
      try {
        const response = await fetch(requestURL, { credentials: 'same-origin', signal: this.designRequest.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Unable to load this design.');
        const html = await response.text();
        if (sequence !== this.designSequence || this.designRequest.signal.aborted || !this.isConnected) return false;
        const saved = selection || this.captureSelection();
        this.commitDesign(html, url, saved, historyMode);
        return true;
      } catch (error) {
        if (sequence !== this.designSequence || error.name === 'AbortError' || !this.isConnected) return false;
        this.designFeedback('Couldn’t load that design. Your selection is unchanged. Tap a design to try again.', true);
        const fallback = this.querySelector('[data-bm-design-fallback]');
        if (fallback) { fallback.href = url.href; fallback.hidden = false; }
        if (historyMode === 'none' && this.currentURL) { history.replaceState(history.state, '', this.currentURL); this.rememberSelection(); }
        return false;
      } finally {
        if (sequence === this.designSequence && this.isConnected) {
          this.switching = false; this.removeAttribute('aria-busy'); this.update();
        }
      }
    }

    commitDesign(html, url, selection, historyMode) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const candidate = parsed.querySelector('bm-product');
      const config = candidate?.querySelector('[data-bm-config]');
      const nextConfig = config && JSON.parse(config.textContent);
      if (!candidate || !nextConfig?.products?.some(product => product.id === nextConfig.productId) || !candidate.querySelector('form')) throw new Error('Incomplete product response.');
      const next = document.importNode(candidate, true);
      next.pendingSelection = selection;
      const scroll = window.scrollY;
      const currentVariant = nextConfig.products.find(product => product.id === nextConfig.productId)?.variants.find(variant => variant.available && selection.single && variant.options.length === selection.single.options.length && variant.options.every((value, index) => value === selection.single.options[index]));
      url.searchParams.delete('section_id');
      if (selection.mode === 'bundle' && nextConfig.bundleEnabled) url.searchParams.set('bundle', '1');
      else url.searchParams.delete('bundle');
      if (currentVariant) url.searchParams.set('variant', currentVariant.id);
      else url.searchParams.delete('variant');
      if (historyMode === 'push') {
        this.rememberSelection();
        history.pushState({ ...history.state, bmProduct: { url: url.href, selection } }, '', url);
      } else history.replaceState({ ...history.state, bmProduct: { url: url.href, selection } }, '', url);
      const structured = parsed.querySelector('script[type="application/ld+json"]');
      const previousStructured = this.closest('.shopify-section')?.querySelector('script[type="application/ld+json"]');
      if (structured && previousStructured) previousStructured.textContent = structured.textContent;
      this.replaceWith(next);
      const selected = next.querySelector('[data-bm-design-link][aria-current="page"]');
      selected?.focus({ preventScroll: true });
      // Keep the user's position while replacing the product, including the
      // browser's next layout/scroll-anchoring pass.
      window.scrollTo({ top: scroll, behavior: 'instant' });
      next.designScrollFrame = requestAnimationFrame(() => {
        next.designScrollFrame = null;
        if (!next.isConnected) return;
        window.scrollTo({ top: scroll, behavior: 'instant' });
      });
      next.designFeedback('Selected: ' + next.product.title);
      document.title = next.product.title + (next.config.shopName ? ' — ' + next.config.shopName : '');
    }

    money(cents) {
      try {
        return new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency }).format(cents / 100);
      } catch { return (cents / 100).toFixed(2) + ' ' + this.config.currency; }
    }

    rowProduct(row) {
      return this.products.find(product => String(product.id) === row.querySelector('[data-bm-row-product]').value);
    }

    rowVariant(row) {
      const product = this.rowProduct(row);
      return product?.variants.find(variant => String(variant.id) === row.querySelector('[data-bm-row-variant]').value);
    }

    singleVariant() {
      const id = this.form.querySelector('[name="id"]:checked')?.value;
      return this.product.variants.find(variant => String(variant.id) === id);
    }

    updateRow(row) {
      const product = this.rowProduct(row), select = row.querySelector('[data-bm-row-variant]');
      select.replaceChildren(new Option('Choose a size', ''));
      if (product) product.variants.forEach(variant => {
        const title = variant.title === 'Default Title' ? 'One size' : variant.title;
        const option = new Option(title + ' — ' + this.money(variant.price) + (variant.available ? '' : ' · Sold out'), String(variant.id));
        option.disabled = !variant.available;
        select.add(option);
      });
      const sizes = row.querySelector('[data-bm-row-sizes]');
      if (sizes) {
        sizes.replaceChildren(...(product?.variants || []).map(variant => {
          const title = variant.title === 'Default Title' ? 'One size' : variant.title;
          const button = document.createElement('button');
          button.type = 'button'; button.className = 'bm-pdp__row-size';
          button.dataset.bmRowSize = String(variant.id);
          button.textContent = { Small: 'S', Medium: 'M', Large: 'L' }[title] || title;
          button.disabled = !variant.available;
          button.setAttribute('aria-label', title + ' — ' + this.money(variant.price) + (variant.available ? '' : ' — Sold out'));
          button.setAttribute('aria-pressed', 'false');
          button.title = title;
          return button;
        }));
        select.hidden = true;
      }
      const image = row.querySelector('[data-bm-row-image]');
      const source = product?.featured_image || (this.config.packagedImages && this.config.images[product?.handle]);
      image.hidden = !source;
      if (source) { image.src = typeof source === 'string' ? source : source.src; image.alt = product.title; }
      row.querySelector('[data-bm-row-price]').textContent = product ? 'From ' + this.money(product.price) : 'No eligible design is available.';
    }

    selectRowSize(button) {
      const row = button.closest('[data-bm-row]');
      const variant = row && this.rowProduct(row)?.variants.find(variant => String(variant.id) === button.dataset.bmRowSize && variant.available);
      if (!variant) return;
      row.querySelector('[data-bm-row-variant]').value = String(variant.id);
      this.clearError(); this.update(); this.rememberSelection();
    }

    rowSizeControl(row) {
      return row.querySelector('[data-bm-row-sizes]')?.querySelector('button:not(:disabled)') || row.querySelector('[data-bm-row-variant]');
    }

    setMode(mode) {
      this.mode = mode;
      const bundle = mode === 'bundle';
      this.querySelector('[data-bm-bundle]').hidden = !bundle;
      this.querySelector('[data-bm-single]').hidden = bundle;
      this.querySelector('[data-bm-single]').disabled = bundle;
      this.rows.forEach(row => row.disabled = !bundle);
    }

    onChange(event) {
      const target = event.target;
      this.clearError();
      if (target.name === 'bm-mode') this.setMode(target.value);
      if (target.matches('[data-bm-row-product]')) this.updateRow(target.closest('[data-bm-row]'));
      if (target.name === 'id') {
        const variant = this.singleVariant();
        if (variant?.featured_media) {
          const index = [...this.querySelectorAll('[data-bm-slide]')].findIndex(slide => String(variant.featured_media.id) === slide.dataset.mediaId);
          if (index >= 0) this.showPhoto(index);
        }
        if (variant) try { const url = new URL(location.href); url.searchParams.set('variant', variant.id); history.replaceState(history.state, '', url); this.currentURL = url; } catch { /* Form submission remains available. */ }
      }
      this.update();
      this.rememberSelection();
    }

    onClick(event) {
      const rowSize = event.target.closest('[data-bm-row-size]');
      if (rowSize) { this.selectRowSize(rowSize); return; }
      const design = event.target.closest('[data-bm-design-link]');
      if (design && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey && (!event.button || event.button === 0)) {
        event.preventDefault(); this.switchDesign(design.href); return;
      }
      const photo = event.target.closest('[data-bm-photo]');
      if (photo) this.showPhoto(Number(photo.dataset.bmPhoto));
      if (event.target.closest('[data-bm-copy]')) this.copySize();
      if (event.target.closest('[data-bm-prev]')) this.stepPhoto(-1);
      if (event.target.closest('[data-bm-next]')) this.stepPhoto(1);
      if (event.target.closest('[data-bm-jump]')) this.goTo(this.form.querySelector('[name="id"]:not(:disabled)') || this.form);
      if (event.target.closest('[data-bm-build-bundle]') && this.config.bundleEnabled) {
        const choice = this.querySelector('[name="bm-mode"][value="bundle"]');
        if (choice) { choice.checked = true; this.setMode('bundle'); this.clearError(); this.update(); this.goTo(this.missingChoice() || choice); }
      }
      if (event.target.closest('[data-bm-sticky-add]')) {
        const missing = this.missingChoice();
        if (missing) this.goTo(missing);
        else this.form.requestSubmit();
      }
    }

    showPhoto(index) {
      const slides = [...this.querySelectorAll('[data-bm-slide]')];
      if (!slides[index]) return;
      this.galleryIndex = index;
      slides.forEach((slide, i) => slide.hidden = i !== index);
      this.querySelectorAll('[data-bm-photo]').forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.bmPhoto) === index)));
      this.querySelector('[data-bm-status]').textContent = 'Product image ' + (index + 1) + ' of ' + slides.length + '.';
      const counter = this.querySelector('[data-bm-photo-counter]');
      if (counter) counter.textContent = (index + 1) + ' / ' + slides.length;
      const note = this.querySelector('[data-bm-photo-note]');
      if (note) note.textContent = (slides[index].dataset.photoLabel || 'Product photography') + (slides[index].dataset.photoLabel?.includes('AI') ? '. Campaign concept; original artwork defines the final print.' : '.');
    }

    stepPhoto(direction) {
      const count = this.querySelectorAll('[data-bm-slide]').length;
      if (count) this.showPhoto(((this.galleryIndex || 0) + direction + count) % count);
    }

    copySize() {
      const first = this.rowVariant(this.rows[0]);
      if (!first) return;
      let copied = 0;
      this.rows.slice(1).forEach(row => {
        const match = this.rowProduct(row)?.variants.find(variant => variant.available && variant.options.length === first.options.length && variant.options.every((value, index) => value === first.options[index]));
        if (match) { row.querySelector('[data-bm-row-variant]').value = String(match.id); copied++; }
      });
      this.querySelector('[data-bm-status]').textContent = copied === 2 ? 'Size applied to all three belts.' : 'Size copied where available. Choose a size for the remaining belts.';
      this.clearError();
      this.update();
      this.rememberSelection();
    }

    missingChoice() {
      if (this.mode === 'bundle') {
        for (const row of this.rows) {
          if (!this.rowProduct(row)?.available) return row.querySelector('[data-bm-row-product]');
          if (!this.rowVariant(row)?.available) return this.rowSizeControl(row);
        }
      } else if (!this.singleVariant()?.available) return this.form.querySelector('[name="id"]:not(:disabled)');
      return null;
    }

    update() {
      const bundle = this.mode === 'bundle', variant = this.singleVariant();
      const prices = this.rows.map(row => this.rowVariant(row)?.price ?? this.rowProduct(row)?.price ?? 0);
      const gross = prices.reduce((sum, price) => sum + price, 0);
      const saving = bundle && this.config.bundleEnabled ? Math.min(...prices) : 0;
      const total = bundle ? gross - saving : (variant?.price ?? this.product.price);
      const expectedSaving = this.config.bundleEnabled ? Math.min(...prices) : 0;
      const expectedTotal = gross - expectedSaving;
      for (const [selector, text] of [
        ['[data-bm-mode-total]', this.money(expectedTotal)], ['[data-bm-mode-gross]', this.money(gross)],
        ['[data-bm-mode-save]', 'Save ' + this.money(expectedSaving) + ' · estimated'],
        ['[data-bm-campaign-total]', this.money(expectedTotal)], ['[data-bm-campaign-gross]', this.money(gross)],
        ['[data-bm-campaign-save]', 'Save ' + this.money(expectedSaving) + ' · estimated']
      ]) { const element = this.querySelector(selector); if (element) element.textContent = text; }
      const selectionShipping = this.querySelector('[data-bm-selection-shipping]');
      if (selectionShipping && this.config.shippingEnabled) {
        selectionShipping.textContent = total >= this.config.shippingThreshold ? 'Free shipping with this selection.' : 'Add ' + this.money(this.config.shippingThreshold - total) + ' more for free shipping. Based on this selection.';
      }
      const ready = this.rows.every(row => this.rowVariant(row)?.available);
      this.querySelector('[data-bm-unit-price]').textContent = this.money(variant?.price ?? this.product.price);
      this.querySelector('[data-bm-total]').textContent = this.money(total);
      this.querySelector('[data-bm-summary-label]').textContent = bundle ? (ready ? 'Estimated pack total' : 'Estimated pack total · from') : 'Your belt';
      this.querySelector('[data-bm-discount-note]').hidden = !bundle;
      this.querySelector('[data-bm-gross]').hidden = !bundle;
      this.querySelector('[data-bm-gross]').textContent = this.money(gross);
      this.querySelector('[data-bm-save]').hidden = !bundle;
      this.querySelector('[data-bm-save]').textContent = 'Save ' + this.money(saving) + ' with the offer';
      this.querySelector('[data-bm-sticky-price]').textContent = this.money(total) + (bundle ? ' estimated' : '');
      this.querySelector('[data-bm-stock]').textContent = variant ? (variant.available ? 'Available in ' + (variant.title === 'Default Title' ? 'one size' : variant.title) + '.' : 'This size is sold out.') : (this.product.available ? 'Choose a size to see availability.' : 'This design is currently sold out.');
      this.rows.forEach(row => {
        const variant = this.rowVariant(row);
        if (variant) row.querySelector('[data-bm-row-price]').textContent = this.money(variant.price);
        row.querySelector('[data-bm-row-sizes]')?.querySelectorAll('[data-bm-row-size]').forEach(button => button.setAttribute('aria-pressed', String(Boolean(variant && String(variant.id) === button.dataset.bmRowSize))));
      });
      const count = this.rows.filter(row => this.rowVariant(row)?.available).length;
      this.querySelector('[data-bm-bundle-status]').textContent = count + ' of 3 sizes chosen.';
      this.querySelector('[data-bm-copy]').disabled = !this.rowVariant(this.rows[0])?.available || this.busy;
      const unavailable = this.config.requiresSellingPlan || (bundle ? !this.products.some(product => product.available && !product.requires_selling_plan) : !this.product.available);
      this.querySelector('[data-bm-submit]').disabled = unavailable || this.busy || this.switching;
      this.querySelector('[data-bm-sticky-add]').disabled = unavailable || this.busy || this.switching;
      this.querySelector('[data-bm-add-label]').textContent = this.busy ? 'Adding…' : unavailable ? 'Unavailable' : bundle ? 'Add three belts — ' + this.money(total) + ' estimated' : this.config.buyLabel + ' — ' + this.money(total);
      this.querySelector('[data-bm-sticky-add]').textContent = this.busy ? 'Adding…' : unavailable ? 'Unavailable' : this.missingChoice() ? 'Choose size' : 'Add to bag';
    }

    goTo(element) {
      if (!element) return;
      element.scrollIntoView({ block: 'center', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      element.focus({ preventScroll: true });
    }

    clearError() {
      this.querySelector('[data-bm-error]').hidden = true;
      this.querySelector('[data-bm-error]').textContent = '';
      this.form.querySelectorAll('[aria-invalid="true"]').forEach(element => element.removeAttribute('aria-invalid'));
    }

    error(message) {
      const error = this.querySelector('[data-bm-error]');
      error.textContent = message;
      error.hidden = false;
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.busy || this.switching) return;
      this.clearError();
      const missing = this.missingChoice();
      if (missing) {
        this.error(this.mode === 'bundle' ? 'Choose an available design and size for every belt.' : 'Choose an available size to add your belt.');
        missing.setAttribute('aria-invalid', 'true');
        this.goTo(missing);
        return;
      }
      if (this.config.requiresSellingPlan || (this.mode === 'single' && !this.singleVariant()?.available)) return;
      const variants = this.mode === 'bundle' ? this.rows.map(row => this.rowVariant(row)) : [this.singleVariant()];
      if (variants.some(variant => !variant?.available)) return;
      const items = variants.map(variant => ({ id: variant.id, quantity: 1 }));
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer?.getSectionsToRender?.() || [];
      drawer?.setActiveElement?.(document.activeElement);
      this.busy = true;
      this.form.setAttribute('aria-busy', 'true');
      this.update();
      let added = false;
      try {
        const body = { items };
        if (sections.length) { body.sections = sections.map(section => section.id); body.sections_url = location.pathname; }
        const response = await fetch(this.config.addUrl, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(body) });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add your selection. Please check availability and try again.');
        added = true;
        if (!drawer || typeof drawer.renderContents !== 'function') { location.assign(this.config.cartUrl); return; }
        const usableSections = sections.every(section => {
          if (!result.sections?.[section.id]) return false;
          const parsed = new DOMParser().parseFromString(result.sections[section.id], 'text/html');
          return Boolean(parsed.querySelector(section.selector || '.shopify-section'));
        });
        if (!usableSections) { location.assign(this.config.cartUrl); return; }
        drawer.classList.remove('is-empty');
        drawer.renderContents(result);
        const cart = await this.refreshCart();
        this.querySelector('[data-bm-status]').textContent = cart ? 'Added to bag. Bag subtotal after discounts: ' + this.money(cart.total_price) + '.' : 'Added to your bag.';
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bm-product', cartData: result }).catch(() => {});
        }
      } catch (error) {
        if (added) location.assign(this.config.cartUrl);
        else this.error(error.message || 'Connection interrupted. Check your bag before trying again.');
      } finally {
        this.busy = false;
        this.form.removeAttribute('aria-busy');
        this.update();
      }
    }

    async refreshCart() {
      const sequence = this.cartSequence = (this.cartSequence || 0) + 1;
      try {
        const response = await fetch(this.config.cartUrl + '.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } });
        if (!response.ok) return null;
        const cart = await response.json();
        if (sequence !== this.cartSequence || this.isConnected === false) return null;
        if (cart.currency && cart.currency !== this.config.shopCurrency) {
          this.querySelector('[data-bm-shipping]').hidden = true;
          const estimate = this.querySelector('[data-bm-selection-shipping]'); if (estimate) estimate.hidden = true;
        } else if (this.config.shippingEnabled) {
          this.querySelector('[data-bm-shipping]').hidden = false;
          const estimate = this.querySelector('[data-bm-selection-shipping]'); if (estimate) estimate.hidden = false;
          this.updateShipping(cart.total_price);
        }
        return cart;
      } catch { return null; }
    }

    updateShipping(total) {
      if (!this.config.shippingEnabled || !(this.config.shippingThreshold > 0)) return;
      const remaining = Math.max(0, this.config.shippingThreshold - total);
      this.querySelector('[data-bm-shipping-message]').textContent = total >= this.config.shippingThreshold ? 'Free shipping unlocked in your current bag.' : 'Your current bag is ' + this.money(remaining) + ' away from free shipping.';
      this.querySelector('[data-bm-shipping-progress]').value = Math.min(total, this.config.shippingThreshold);
    }
  });
}
